import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { toast } from 'react-toastify';
import CustomerPortfolioService from '../service';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import ModalConfirmation from '../../../../components/Modal/ConfirmationModal';
import { useHistory } from 'react-router-dom';

import {
  Container,
  Row,
  Card,
  Form,
  Col,
  Input,
  CardHeader,
  CardBody
} from 'reactstrap';
import ReactLoader from 'components/Loader';
import ReactButton from 'components/Buttons/ReactButton';
import { ScreenPrompt } from 'components/ScreenPrompt';
import Select from 'react-select';
import AccountService from '../../../CRM/Account/services/accountService';
import { custom_select } from 'assets/styles/multiple_select';
import { useRecordTypes } from '../../../RecordType/hooks/useRecordTypes';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';

const formatAccountsToSelect = (accounts) => {
  return accounts.map((account) => ({
    label: account.name,
    value: account.id
  }));
};

const CustomerPortfolioForm = (props) => {
  const history = useHistory();
  const { id } = props.match.params;
  const {
    reset,
    control,
    handleSubmit,
    errors,
    formState,
    setValue,
    watch,
    getValues
  } = useForm();
  const { isDirty } = formState;
  const { recordTypes, isLoadingRecordTypes } = useRecordTypes(
    entitiesName.PROTOCOL
  );
  const [isUpdate, setIsUpdate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accountIds, setAccountIds] = useState([]);
  const [selectedRecordType, setSelectedRecordType] = useState(null);
  const [customerPortfolio, setCustomerPortfolio] = useState(null);
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);

  useEffect(() => {
    setLoading(true);
    AccountService.getAllAccounts()
      .then((res) => {
        const accounts = res.data.data;
        setAccountIds(accounts);
        setLoading(false);
      })
      .catch((err) => {
        const { data } = err.response;
        toast.error(data.message || 'Erro ao listar contas!', {
          autoClose: 3000,
          closeOnClick: true
        });
      });
  }, []);

  useEffect(() => {
    if (recordTypes && !!recordTypes.length) {
      const formattedRecordTypes = recordTypes.map((group) => ({
        label: group.name,
        value: group.id
      }));
      setSelectedRecordType(formattedRecordTypes);
    }
  }, [recordTypes]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      CustomerPortfolioService.get(id)
        .then((res) => {
          const getCustomerPortfolio = res.data.content;
          reset({ ...getCustomerPortfolio });
          setCustomerPortfolio(getCustomerPortfolio);
          setIsUpdate(true);
          setLoading(false);
        })
        .catch((err) => {
          console.error(err);
          toast.error(
            err.response?.data?.message ||
              err.message ||
              'Erro ao buscar categoria de cliente!'
          );
        });
    }
  }, [id, reset]);

  useEffect(() => {
    if (id && accountIds && accountIds.length > 0 && customerPortfolio) {
      const filteredAccounts = accountIds.filter(
        (account) => account.customer_portfolio_id === parseInt(id)
      );
      const formattedAccounts = filteredAccounts.map((account) => ({
        label: account.name,
        value: account.id
      }));
      setValue('accountIds', formattedAccounts);
    }
  }, [id, accountIds, setValue, customerPortfolio]);

  useEffect(() => {
    if (
      id &&
      selectedRecordType &&
      selectedRecordType.length > 0 &&
      customerPortfolio
    ) {
      const customerPortfolioRecordType = selectedRecordType.find(
        (recordType) => recordType.value === customerPortfolio.record_type_id
      );
      setValue('recordTypeId', customerPortfolioRecordType);
    }
  }, [customerPortfolio, id, selectedRecordType, setValue]);

  const onSubmit = async (data, isSubmittingFromModal) => {
    if (isUpdate && !data.recordTypeId && !isSubmittingFromModal) {
      setOpenModalConfirmation(true);
      return;
    }
    try {
      const formatedData = {
        ...data,
        recordTypeId: data.recordTypeId ? data.recordTypeId.value : null
      };

      if (!formatedData.recordTypeId) {
        toast.error('Tipo de registro é obrigatório!', {
          autoClose: 3000,
          closeOnClick: true
        });
        return;
      }

      if (
        !Array.isArray(formatedData.accountIds) ||
        formatedData.accountIds.length === 0
      ) {
        toast.error('Selecione pelo menos um membro!', {
          autoClose: 3000,
          closeOnClick: true
        });
        return;
      }

      const accountIds = formatedData.accountIds.map(
        (account) => account.value
      );
      formatedData.accountIds = accountIds;
      const usrLogin = localStorage.getItem('login');

      isUpdate
        ? await CustomerPortfolioService.updateCustomerPortfolio(
            id,
            formatedData,
            usrLogin
          )
        : await CustomerPortfolioService.createCustomerPortfolio(
            formatedData,
            usrLogin
          );

      history.push('/admin/customer-portfolio');
      toast.success('Categoria de Cliente criada com sucesso', {
        autoClose: 3000,
        closeOnClick: true
      });
    } catch (err) {
      console.error('------->', err);
      const { data } = err.response;
      toast.error(data.message || 'Erro ao cadastrar categoria de cliente!', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    history.push('/admin/customer-portfolio');
  };

  return (
    <>
      <SimpleHeader
        name="Form validation"
        parentName="Forms"
        returnPath="/admin/customer-portfolio"
        buttonTitle="Voltar para a pagina de categoria de cliente"
      />
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message={`Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar`}
      />
      <Container className="mt--6" fluid>
        <Row className="mb-3">
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">Categoria dos Clientes</h3>
                </CardHeader>
                <CardBody>
                  {loading && isLoadingRecordTypes ? (
                    <Row className="justify-content-md-center">
                      <ReactLoader />
                    </Row>
                  ) : (
                    <Form
                      className="needs-validation"
                      onSubmit={handleSubmit(() => {
                        onSubmit(getValues(), false);
                      })}
                    >
                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">Nome*</label>
                          <Controller
                            as={Input}
                            control={control}
                            name="name"
                            rules={{ required: 'Nome é obrigatório!' }}
                            defaultValue=""
                          />
                          <ErrorMessage
                            errors={errors}
                            name="name"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                        <Col md="6">
                          <label className="form-control-label">Membros*</label>
                          <Controller
                            control={control}
                            name="accountIds"
                            rules={{
                              required: 'Selecione pelo menos um membro!'
                            }}
                            defaultValue={[]}
                            render={() => (
                              <Select
                                styles={{ ...custom_select, width: '100%' }}
                                placeholder="Selecione as contas"
                                options={
                                  formatAccountsToSelect(accountIds) || []
                                }
                                isMulti={true}
                                isClearable={true}
                                closeMenuOnSelect={false}
                                valueController={setValue}
                                name="accountIds"
                                value={watch('accountIds') || null}
                                onChange={(value) => {
                                  setValue('accountIds', value);
                                }}
                                defaultValue={[]}
                              />
                            )}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="accountIds"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">
                            Tipo de Registro*
                          </label>
                          <Controller
                            render={() => (
                              <Select
                                styles={{ ...custom_select, width: '100%' }}
                                placeholder="Selecione o tipo de registro"
                                options={selectedRecordType || []}
                                isMulti={false}
                                isClearable={true}
                                closeMenuOnSelect={true}
                                valueController={setValue}
                                name="recordTypeId"
                                value={watch('recordTypeId') || null}
                                onChange={(value) => {
                                  setValue('recordTypeId', value);
                                }}
                                defaultValue={''}
                                rules={{
                                  required: 'Tipo de registro é obrigatório!'
                                }}
                              />
                            )}
                            control={control}
                            name="recordTypeId"
                            defaultValue={''}
                          />

                          <ErrorMessage
                            errors={errors}
                            name="recordTypeId"
                            render={({ message }) => (
                              <p style={{ color: 'red' }}>{message}</p>
                            )}
                          />
                        </Col>
                      </Row>
                      <hr />

                      <ReactButton btnColor="confirmation" type="submit">
                        Salvar
                      </ReactButton>

                      <ReactButton
                        btnColor="cancelation"
                        onClick={() => cancelAction()}
                      >
                        Cancelar
                      </ReactButton>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
      {isUpdate && customerPortfolio && (
        <ModalConfirmation
          isModalOpen={openModalConfirmation}
          onModalToggle={setOpenModalConfirmation}
          unmountOnClose={true}
          deleteResource={() => {
            setOpenModalConfirmation(false);
            const data = getValues();
            onSubmit(data, true);
          }}
          message={
            customerPortfolio && customerPortfolio.sla_matrix ? (
              <span>
                ATENÇÃO! <br />
                Deseja realmente remover o tipo de registro dessa categoria de
                cliente? <br /> <br />
                Essa categoria possui a Matriz SLA: <br />
                <strong>
                  &apos;{customerPortfolio.sla_matrix.name}
                  &apos;{' '}
                </strong>{' '}
                vinculada a ela. <br /> <br />
                Ao remover o tipo de registro, a Matriz de SLA será{' '}
                <strong
                  style={{
                    color: 'red'
                  }}
                >
                  Desabilitada
                </strong>{' '}
                e todas as suas classificações de serviço serão{' '}
                <strong
                  style={{
                    color: 'red'
                  }}
                >
                  Excluidas
                </strong>
                .<br /> <br />
                Deseja continuar mesmo assim?
              </span>
            ) : (
              'Deseja realmente remover o tipo de registro dessa categoria de cliente?'
            )
          }
        />
      )}
    </>
  );
};

export default CustomerPortfolioForm;
